import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';
import { getSharedRentals } from '../../api/sharedRentalsApi';
import { getAvailabilityByMonth } from '../../api/availabilityApi';
import { createSharedRentalInterest, getRestPaymentInfo, getInterestPaymentInfo, initiateRestPayment, cancelSharedRentalInterest } from '../../api/sharedRentalInterestsApi';
import { checkOutWithKhalti } from '../../helpers/payments';
import { PaymentMethodModal } from '../payments';
import { getCardVisibilityState, slotSortOrder } from '../../helpers/getCardVisibilityState';
import { getCurrencyRate } from '../../api/paymentApi';
import { BASE_URL } from '../../constants';
import SharedRentalCard from '../groupTours/SharedRentalCard';
import { formatMoneyLegacy } from '../../utils/currency';
import { Seo, PackagesGroupTripsBar } from '../shared';
import '../../styles/pkgs-gt-bar.scss';

/** Build exactly 12 months starting from the current month (no past months). */
function buildMonthOptions() {
  const opts = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() + i, 1);
    opts.push({ value: `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`, label: x.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) });
  }
  return opts;
}

export function SharedRentalsContent() {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.userStore?.currentUser) || {};
  const [availableRentals, setAvailableRentals] = useState([]);
  const [availablePage, setAvailablePage] = useState(0);
  const [availableMeta, setAvailableMeta] = useState(null);
  const [loadingAvailable, setLoadingAvailable] = useState(true);
  const [closedRentals, setClosedRentals] = useState([]);
  const [closedPage, setClosedPage] = useState(0);
  const [closedMeta, setClosedMeta] = useState(null);
  const [loadingClosed, setLoadingClosed] = useState(false);
  const [closedExpanded, setClosedExpanded] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const currentMonthValue = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [payRestInfo, setPayRestInfo] = useState(null);
  const [payRestIdx, setPayRestIdx] = useState(null);
  const [payRestPaying, setPayRestPaying] = useState(false);
  const [payInterestInfo, setPayInterestInfo] = useState(null);
  const [payInterestIdx, setPayInterestIdx] = useState(null);
  const [payInterestPaying, setPayInterestPaying] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [availabilityByMonth, setAvailabilityByMonth] = useState({});
  const [usdToNprRate, setUsdToNprRate] = useState(null);
  const isInitialFetch = useRef(true);
  const hasAutoSelectedFirstAvailable = useRef(false);

  const monthOpts = buildMonthOptions();
  const PER_PAGE = 12;

  const fetchAvailable = (yearMonth, page, append) => {
    return getSharedRentals({
      year_month: yearMonth || undefined,
      status: 'available',
      page: page || 1,
      per_page: PER_PAGE
    }).then((r) => {
      const list = r.data?.shared_rentals || r.data || [];
      const meta = r.data?.meta?.pagination || r.meta?.pagination || {};
      if (append) {
        setAvailableRentals((prev) => [...prev, ...list]);
      } else {
        setAvailableRentals(list);
      }
      setAvailablePage(meta.current_page || page || 1);
      setAvailableMeta(meta);
    });
  };

  const fetchClosed = (yearMonth, page, append) => {
    return getSharedRentals({
      year_month: yearMonth || undefined,
      status: 'closed',
      page: page || 1,
      per_page: PER_PAGE
    }).then((r) => {
      const list = r.data?.shared_rentals || r.data || [];
      const meta = r.data?.meta?.pagination || r.meta?.pagination || {};
      if (append) {
        setClosedRentals((prev) => [...prev, ...list]);
      } else {
        setClosedRentals(list);
      }
      setClosedPage(meta.current_page || page || 1);
      setClosedMeta(meta);
    });
  };

  // When calendar month rolls over, reset to current month if selected month is no longer in 12-month window
  const currentMonthKey = `${new Date().getFullYear()}-${new Date().getMonth()}`;
  useEffect(() => {
    if (!monthOpts.some((o) => o.value === selectedMonth)) {
      setSelectedMonth(currentMonthValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonthKey]);

  useEffect(() => {
    getAvailabilityByMonth()
      .then((r) => setAvailabilityByMonth(r.data || {}))
      .catch(() => setAvailabilityByMonth({}));
  }, []);

  useEffect(() => {
    getCurrencyRate()
      .then((r) => setUsdToNprRate(Number(r.data?.usd_to_npr) || null))
      .catch(() => setUsdToNprRate(null));
  }, []);

  // Default to first available month when current month has no rentals (once per load)
  useEffect(() => {
    if (hasAutoSelectedFirstAvailable.current || !monthOpts.length) return;
    const data = availabilityByMonth;
    if (typeof data !== 'object' || Object.keys(data).length === 0) return;
    const currentHasRentals = (data[currentMonthValue]?.shared_rentals || 0) > 0;
    if (currentHasRentals) return;
    const first = monthOpts.find((o) => (data[o.value]?.shared_rentals || 0) > 0);
    if (first) {
      hasAutoSelectedFirstAvailable.current = true;
      setSelectedMonth(first.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityByMonth, currentMonthValue]);

  useEffect(() => {
    const ym = monthOpts.some((o) => o.value === selectedMonth) ? selectedMonth : currentMonthValue;
    if (isInitialFetch.current) {
      setLoadingAvailable(true);
      isInitialFetch.current = false;
    }
    setClosedExpanded(false);
    setClosedRentals([]);
    setClosedPage(0);
    setClosedMeta(null);
    fetchAvailable(ym, 1, false)
      .catch(() => setAvailableRentals([]))
      .finally(() => setLoadingAvailable(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const pr = q.get('pay_rest');
    const pi = q.get('pay_interest');
    if (pr) {
      setPayRestIdx(pr);
      setPayInterestIdx(null);
      setPayInterestInfo(null);
      getRestPaymentInfo(pr)
        .then((r) => setPayRestInfo(r.data || { can_pay: false }))
        .catch(() => setPayRestInfo({ can_pay: false }));
    } else if (pi) {
      setPayInterestIdx(pi);
      setPayRestIdx(null);
      setPayRestInfo(null);
      getInterestPaymentInfo(pi)
        .then((r) => setPayInterestInfo(r.data || { can_pay: false }))
        .catch(() => setPayInterestInfo({ can_pay: false }));
    } else {
      setPayRestIdx(null);
      setPayRestInfo(null);
      setPayInterestIdx(null);
      setPayInterestInfo(null);
    }
  }, []);

  const setMonth = (v) => {
    if (monthOpts.some((o) => o.value === v)) setSelectedMonth(v);
  };

  useEffect(() => {
    if (!closedExpanded) return;
    const ym = monthOpts.some((o) => o.value === selectedMonth) ? selectedMonth : currentMonthValue;
    if (closedRentals.length === 0 && !loadingClosed && closedPage === 0) {
      setLoadingClosed(true);
      fetchClosed(ym, 1, false)
        .catch(() => setClosedRentals([]))
        .finally(() => setLoadingClosed(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedExpanded]);

  const handlePayRest = () => {
    if (!payRestIdx || !payRestInfo?.can_pay || payRestPaying) return;
    setPayRestPaying(true);
    initiateRestPayment(payRestIdx)
      .then((r) => {
        if (r.data?.idx != null && r.data?.amount != null) {
          setPaymentModalData({
            productIdentity: r.data.idx,
            productName: 'SHARED_RENTAL_REST',
            productUrl: `${BASE_URL || ''}/shared-rentals`,
            amount: Number(r.data.amount),
            currencyCode: payRestInfo.currency_code || r.data.currency_code || 'NPR'
          });
          setShowPaymentModal(true);
        } else {
          swal('Error', 'Could not start payment.', 'error');
        }
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not start payment.', 'error'))
      .finally(() => setPayRestPaying(false));
  };

  const handlePayInterest = () => {
    if (!payInterestIdx || !payInterestInfo?.can_pay || payInterestPaying) return;
    setPaymentModalData({
      productIdentity: payInterestIdx,
      productName: 'SHARED_RENTAL',
      productUrl: `${BASE_URL || ''}/shared-rentals`,
      amount: Number(payInterestInfo.amount || 0),
      currencyCode: payInterestInfo.currency_code || 'NPR'
    });
    setShowPaymentModal(true);
  };

  const availableList = (Array.isArray(availableRentals) ? availableRentals : []).slice().sort((a, b) => slotSortOrder(a) - slotSortOrder(b));
  const closedList = (Array.isArray(closedRentals) ? closedRentals : []).slice().sort((a, b) => slotSortOrder(a) - slotSortOrder(b));
  const availableHasMore = availableMeta && (availableMeta.current_page || 0) < (availableMeta.total_pages || 0);
  const closedHasMore = closedMeta && (closedMeta.current_page || 0) < (closedMeta.total_pages || 0);
  const ym = monthOpts.some((o) => o.value === selectedMonth) ? selectedMonth : currentMonthValue;

  const handleSelect = (rental) => {
    if (!(currentUser && (currentUser.idx || currentUser.email))) {
      const returnTo = window.location.pathname + (window.location.search || '');
      window.location.href = '/login?return=' + encodeURIComponent(returnTo || '/shared-rentals');
      return;
    }
    const idx = rental && rental.idx != null ? rental.idx : null;
    setSelectedIdx((prev) => (prev === idx) ? null : idx);
  };

  const handleSubmitInterest = (rental, values) => {
    setSubmitting(true);
    const payload = { ...values, shared_rental_id: rental.idx };
    if (!payload.number_of_people) delete payload.number_of_people;
    return createSharedRentalInterest(payload)
      .then((res) => {
        setSelectedIdx(null);
        const pay = (res.data && res.data.payment) || res.data;
        const idx = pay && pay.idx;
        const amount = (pay && pay.amount) != null ? pay.amount : rental.interest_amount;
        if (idx != null && amount != null) {
          setPaymentModalData({
            productIdentity: idx,
            productName: 'SHARED_RENTAL',
            productUrl: `${BASE_URL || ''}/shared-rentals`,
            amount: Number(amount),
            currencyCode: rental.currency_code || 'NPR'
          });
          setShowPaymentModal(true);
        } else {
          swal('Error', 'Could not open payment.', 'error');
        }
      })
      .catch((err) => {
        const is401 = err.response && err.response.status === 401;
        const data = err.response?.data || {};
        if (is401) {
          window.location.href = '/login?return=' + encodeURIComponent('/shared-rentals');
          throw err;
        }
        if (err.response?.status === 422 && data.existing_interest && data.allow_cancel) {
          const msg = data.error || 'You already have a pending booking. Complete or cancel it before changing seat count.';
          const interestIdx = data.interest_idx; // idx string for PATCH .../shared_rental_interests/:idx/cancel
          swal({
            title: 'Pending booking',
            text: msg,
            icon: 'warning',
            buttons: {
              cancel: 'OK',
              confirm: 'Cancel pending booking'
            }
          }).then((value) => {
            if (value === true && interestIdx) {
              return cancelSharedRentalInterest(interestIdx)
                .then(() => {
                  swal('Done', 'Your pending booking was cancelled. You can show interest again with a different number of seats.', 'success');
                  setSelectedIdx(null);
                })
                .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not cancel.', 'error'));
            }
          });
        } else {
          const msg = (data.errors && data.errors.join(' ')) || err.message || 'Something went wrong.';
          swal('Error', msg, 'error');
        }
        throw err;
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Seo
        title="Shared Rides"
        path="/shared-rentals"
        noindex
        description="Share intercity rides across Nepal with VisitAllNepal — Kathmandu, Pokhara, Chitwan and more. Book securely and split the cost."
      />
      <PackagesGroupTripsBar />
      <header className="packages-hero packages-hero--video" role="banner">
        <div className="packages-hero__bg-video" aria-hidden="true">
          <video className="packages-hero__video" autoPlay muted loop playsInline poster="https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=1920">
            <source src="https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-snowy-mountain-peaks-4173766-large.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="container text-center">
          <h1 className="text-white text-larger mb-2">Share the Ride. Split the Cost.</h1>
          <p className="text-white mb-0 packages-hero__sub" style={{ opacity: 0.95, fontSize: '1.1rem' }}>
            Fixed routes and dates. Book with VisitAllNepal — share the ride between cities and split the cost with fellow travelers.
          </p>
        </div>
      </header>

      <section className="categories-section group-tour-cards-section" aria-label="Shared rentals list">
        <div className="container">
          {loadingAvailable && availableRentals.length === 0 && (
            <div className="group-trips-loading" role="status" aria-live="polite" aria-busy="true">
              <span className="group-trips-loading__spinner" aria-hidden="true" />
              <p className="text-muted mb-0">Loading rentals…</p>
            </div>
          )}

          {(!loadingAvailable || availableRentals.length > 0) && (
            <>
              <div className="group-trips-month-filter group-trips-month-pills mb-4">
                <h6 className="group-trips-month-filter__title">
                  <i className="fas fa-calendar-alt" aria-hidden="true" />
                  Show rentals by month
                </h6>
                <p className="group-trips-month-filter__hint">
                  Select a month to see shared rentals. Green months have rentals – click to view.
                </p>
                <div className="group-trips-month-legend" role="presentation">
                  <span className="group-trips-month-legend__item group-trips-month-legend__item--available">
                    <span className="group-trips-month-legend__dot" aria-hidden="true" />
                    <span>Has rentals – click to see</span>
                  </span>
                </div>
                <div className="group-trips-month-pills__list" role="tablist" aria-label="Select month">
                  {monthOpts.map(({ value, label }) => {
                    const rentalCount = availabilityByMonth[value]?.shared_rentals || 0;
                    const hasRentals = rentalCount > 0;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={selectedMonth === value}
                        aria-label={`${label}${value === currentMonthValue ? ' (this month)' : ''}${hasRentals ? `; ${rentalCount} rental(s) – click to see` : ''}`}
                        className={`group-trips-month-pill ${selectedMonth === value ? 'group-trips-month-pill--active' : ''} ${value === currentMonthValue ? 'group-trips-month-pill--this-month' : ''} ${hasRentals ? 'group-trips-month-pill--has-availability' : ''}`}
                        onClick={() => setMonth(value)}
                        title={hasRentals ? `${rentalCount} rental${rentalCount !== 1 ? 's' : ''} in ${label} – click to see` : undefined}
                      >
                        <span className="group-trips-month-pill__label">{label}{value === currentMonthValue ? ' (this month)' : ''}</span>
                        {hasRentals && (
                          <span className="group-trips-month-pill__available" aria-hidden="true">
                            <i className="fas fa-car group-trips-month-pill__icon" aria-hidden="true" />
                            <span>{rentalCount} rental{rentalCount !== 1 ? 's' : ''}</span>
                            <span className="group-trips-month-pill__cta"> – click to see</span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {payRestInfo && (
                <div className="mb-4 p-3 rounded" style={{ background: payRestInfo.can_pay ? '#e6f4ea' : '#fff4e5', border: `1px solid ${payRestInfo.can_pay ? '#34a853' : '#f9ab00'}` }}>
                  {payRestInfo.can_pay ? (
                    <>
                      <div className="d-flex flex-wrap align-items-center justify-content-between">
                        <div>
                          <strong>Pay remaining amount for your shared rental</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>{payRestInfo.rental_name || 'Shared rental'} — {formatMoneyLegacy(payRestInfo.amount ?? 0, payRestInfo.currency_code || 'NPR')}</p>
                        </div>
                        <button type="button" className="btn btn-success mt-2 mt-md-0" onClick={handlePayRest} disabled={!!payRestPaying}>
                          {payRestPaying ? 'Opening…' : 'Pay'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="mb-0">
                      {payRestInfo.reason === 'registration_closed_by_cutoff'
                        ? 'Registration has closed (within the cutoff period before the rental). Rest payment is no longer available.'
                        : payRestInfo.reason === 'registration_closed'
                          ? 'Registration is closed for this rental. Rest payment is no longer available.'
                          : 'Rest payment is not available for this link (already paid, invalid, or the rental is no longer eligible).'}
                    </p>
                  )}
                </div>
              )}

              {payInterestInfo && (
                <div className="mb-4 p-3 rounded" style={{ background: payInterestInfo.can_pay ? '#e6f4ea' : '#fff4e5', border: `1px solid ${payInterestInfo.can_pay ? '#34a853' : '#f9ab00'}` }}>
                  {payInterestInfo.can_pay ? (
                    <>
                      <div className="d-flex flex-wrap align-items-center justify-content-between">
                        <div>
                          <strong>Pay interest to reserve your spot</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>{payInterestInfo.rental_name || 'Shared rental'} — {formatMoneyLegacy(payInterestInfo.amount ?? 0, payInterestInfo.currency_code || 'NPR')}</p>
                        </div>
                        <button type="button" className="btn btn-success mt-2 mt-md-0" onClick={handlePayInterest} disabled={!!payInterestPaying}>
                          {payInterestPaying ? 'Opening…' : 'Pay'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="mb-0">
                      {payInterestInfo.reason === 'slot_full'
                        ? 'This shared rental is full. Your payment cannot be applied.'
                        : payInterestInfo.reason === 'registration_closed_by_cutoff'
                          ? 'Registration has closed (within the cutoff period before the rental). Interest payment is no longer available.'
                          : 'Interest payment is not available for this link (already paid, invalid, or the rental is no longer eligible).'}
                    </p>
                  )}
                </div>
              )}

              <div className="group-trips-main">
                <div className="group-trips-main__content">
                  {availableList.length === 0 && !loadingAvailable && (
                    <div className="group-trips-empty text-center py-4" role="status" aria-live="polite">
                      <i className="fas fa-car text-muted" style={{ fontSize: '2rem', opacity: 0.6 }} aria-hidden="true" />
                      <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.95rem' }}>No shared rentals for this month at the moment. Try another month or check back later.</p>
                    </div>
                  )}
                  {availableList.length > 0 && (
                    <>
                      <h2 className="group-trips-section-title" style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Available Rentals</h2>
                      <div className="group-tour-cards">
                        {availableList.map((r) => {
                          const visibilityState = getCardVisibilityState(r, 'shared_rental');
                          const canReg = !visibilityState.disabled;
                          return (
                            <SharedRentalCard
                              key={r.idx}
                              rental={r}
                              variant="public"
                              visibilityState={visibilityState}
                              currentUser={currentUser}
                              onSelect={canReg ? handleSelect : undefined}
                              isSelected={selectedIdx != null && selectedIdx === r.idx}
                              onSubmitInterest={canReg ? handleSubmitInterest : undefined}
                              submitting={submitting}
                              t={t}
                              usdToNprRate={usdToNprRate}
                            />
                          );
                        })}
                      </div>
                      {availableHasMore && (
                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={loadingAvailable}
                            onClick={() => {
                              setLoadingAvailable(true);
                              fetchAvailable(ym, (availableMeta?.current_page || 1) + 1, true)
                                .catch(() => {})
                                .finally(() => setLoadingAvailable(false));
                            }}
                          >
                            {loadingAvailable ? 'Loading…' : 'Load more'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  <div className="group-trips-closed-section mt-4">
                    <button
                      type="button"
                      className="group-trips-closed-toggle btn btn-outline-secondary btn-sm d-flex align-items-center"
                      onClick={() => setClosedExpanded(!closedExpanded)}
                      aria-expanded={closedExpanded}
                      aria-controls="shared-rentals-closed-list"
                    >
                      <i className={`fas fa-chevron-${closedExpanded ? 'up' : 'down'}`} style={{ marginRight: '0.5rem' }} aria-hidden="true" />
                      Fully Booked / Registration Closed
                    </button>
                    {closedExpanded && (
                      <div id="shared-rentals-closed-list" className="mt-3">
                        {loadingClosed && closedList.length === 0 && (
                          <div className="group-trips-loading py-3" role="status">
                            <span className="group-trips-loading__spinner" aria-hidden="true" />
                            <p className="text-muted mb-0 small">Loading…</p>
                          </div>
                        )}
                        {!loadingClosed && closedList.length === 0 && (
                          <p className="text-muted small mb-0">No closed or fully booked rentals for this month.</p>
                        )}
                        {closedList.length > 0 && (
                          <>
                            <div className="group-tour-cards group-tour-cards--closed">
                              {closedList.map((r) => {
                                const visibilityState = getCardVisibilityState(r, 'shared_rental');
                                return (
                                  <SharedRentalCard
                                    key={r.idx}
                                    rental={r}
                                    variant="public"
                                    visibilityState={visibilityState}
                                    currentUser={currentUser}
                                    onSelect={undefined}
                                    isSelected={false}
                                    onSubmitInterest={undefined}
                                    submitting={false}
                                    t={t}
                                    usdToNprRate={usdToNprRate}
                                  />
                                );
                              })}
                            </div>
                            {closedHasMore && (
                              <div className="text-center mt-3">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  disabled={loadingClosed}
                                  onClick={() => {
                                    setLoadingClosed(true);
                                    fetchClosed(ym, (closedMeta?.current_page || 1) + 1, true)
                                      .catch(() => {})
                                      .finally(() => setLoadingClosed(false));
                                  }}
                                >
                                  {loadingClosed ? 'Loading…' : 'Load more'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <aside className="group-trips-help" aria-label="How shared rentals work">
                  <div className="group-trips-help__inner">
                    <h3 className="group-trips-help__title">
                      <i className="fas fa-lightbulb" aria-hidden="true" />
                      How it works
                    </h3>
                    <div className="group-trips-help__grid">
                      <div className="group-trips-help__item">
                        <i className="fas fa-mouse-pointer" aria-hidden="true" />
                        <div>
                          <span className="group-trips-help__label">Show interest</span>
                          <p className="group-trips-help__text">Click a card and pay the interest amount to reserve your spot. When the team reaches the minimum size, we&apos;ll email you — then you pay the rest. We use a fixed USD↔NPR conversion rate so you know the amount in advance.</p>
                        </div>
                      </div>
                      <div className="group-trips-help__item">
                        <i className="fas fa-info-circle" aria-hidden="true" />
                        <div>
                          <span className="group-trips-help__label">Refunds</span>
                          <p className="group-trips-help__text">Interest is refunded if the group does not form. Once the group is confirmed, payment is non-refundable if you cancel. If you&apos;re removed by admin, your slot may reopen for others.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="shared-rentals-steps" aria-label="What happens">
        <div className="container">
          <p className="shared-rentals-steps__label">What happens</p>
          <div className="shared-rentals-steps__list">
            <div className="shared-rentals-steps__item"><i className="fas fa-ticket-alt" aria-hidden="true" /><span>Reserve your seat</span><small>Pay a small interest</small></div>
            <span className="shared-rentals-steps__arrow" aria-hidden="true">→</span>
            <div className="shared-rentals-steps__item"><i className="fas fa-users" aria-hidden="true" /><span>Group fills</span><small>We gather enough people</small></div>
            <span className="shared-rentals-steps__arrow" aria-hidden="true">→</span>
            <div className="shared-rentals-steps__item"><i className="fas fa-bell" aria-hidden="true" /><span>We notify you</span><small>When the group is ready</small></div>
            <span className="shared-rentals-steps__arrow" aria-hidden="true">→</span>
            <div className="shared-rentals-steps__item"><i className="fas fa-road" aria-hidden="true" /><span>Pay rest &amp; go</span><small>Hit the road together</small></div>
          </div>
        </div>
      </section>

      <section className="shared-rentals-value" aria-label="Popular routes">
        <div className="container">
          <p className="shared-rentals-value__text"><i className="fas fa-car-side shared-rentals-value__icon" aria-hidden="true" /> Share the ride. Split the cost. Join others going the same way.</p>
          <div className="shared-rentals-value__pills">
            <span className="shared-rentals-value__pill">Pokhara – Kathmandu</span>
            <span className="shared-rentals-value__pill">Chitwan</span>
            <span className="shared-rentals-value__pill">Lumbini</span>
          </div>
        </div>
      </section>
      <PaymentMethodModal
        open={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPaymentModalData(null); }}
        paymentParams={paymentModalData}
        amount={paymentModalData?.amount}
      />
    </>
  );
}

export default function SharedRentalsPage() {
  return <SharedRentalsContent />;
}
